import {
  MCP_SKILLS_READ_SCOPE,
  MCP_SKILLS_WRITE_SCOPE,
} from "@arti/schemas/mcpScopes";

import type {
  DashboardMcpGrant,
  DashboardMcpToken,
} from "../../dashboard-client";

export type SkillMeshClientId =
  | "claude-code"
  | "claude-desktop"
  | "codex-cli"
  | "codex-app";

export type SkillMeshAuthMethod = "oauth" | "token";

export interface SkillMeshClient {
  description: string;
  id: SkillMeshClientId;
  kind: "Desktop app" | "Terminal";
  label: string;
  note: { body: string; title: string } | null;
  /** Desktop apps connect from the vendor's cloud or can't read shell
   * environment variables, so they only offer sign-in. */
  signInOnly: boolean;
}

export const SKILL_MESH_CLIENTS: readonly SkillMeshClient[] = [
  {
    description:
      "Anthropic’s coding agent in your terminal. One command adds Skill Mesh to every project on this machine.",
    id: "claude-code",
    kind: "Terminal",
    label: "Claude Code",
    note: null,
    signInOnly: false,
  },
  {
    description:
      "The Claude app for Mac and Windows. Adds Skill Mesh as a custom connector.",
    id: "claude-desktop",
    kind: "Desktop app",
    label: "Claude Desktop",
    note: {
      body: "Claude Desktop reaches Skill Mesh from Anthropic’s servers, so it always signs in with ARTI. You never paste a token, and Advanced settings stay empty because ARTI registers Claude automatically.",
      title: "Signs in from Anthropic’s cloud",
    },
    signInOnly: true,
  },
  {
    description:
      "OpenAI’s coding agent in your terminal. Setup is shared with the Codex app and IDE extension.",
    id: "codex-cli",
    kind: "Terminal",
    label: "Codex CLI",
    note: null,
    signInOnly: false,
  },
  {
    description:
      "The Codex desktop app. Setup is shared with Codex CLI and the IDE extension.",
    id: "codex-app",
    kind: "Desktop app",
    label: "Codex app",
    note: {
      body: "The Codex app shares ~/.codex/config.toml and its sign-in with Codex CLI and the IDE extension. Desktop apps don’t see your shell’s environment variables, so sign in instead of using a token.",
      title: "Set up once for every Codex client",
    },
    signInOnly: true,
  },
];

export function getSkillMeshClient(id: SkillMeshClientId): SkillMeshClient {
  const client = SKILL_MESH_CLIENTS.find((candidate) => candidate.id === id);
  if (!client) {
    throw new Error(`Unknown Skill Mesh client: ${id}`);
  }
  return client;
}

/** Sign-in-only clients ignore a token choice made for another client. */
export function resolveSkillMeshAuthMethod(
  client: SkillMeshClient,
  method: SkillMeshAuthMethod,
): SkillMeshAuthMethod {
  return client.signInOnly ? "oauth" : method;
}

export const SKILL_MESH_SERVER_NAME = "skill-mesh";
export const SKILL_MESH_TOKEN_ENV_VAR = "ARTI_SKILL_MESH_TOKEN";
export const SKILL_MESH_TOKEN_MASK = "•".repeat(40);

export function skillMeshTokenScopes(write: boolean): string[] {
  return write
    ? [MCP_SKILLS_READ_SCOPE, MCP_SKILLS_WRITE_SCOPE]
    : [MCP_SKILLS_READ_SCOPE];
}

export interface SkillMeshCommand {
  /** Full text for the clipboard, including the token when there is one. */
  copy: string;
  /** Text to render: the token stays masked until the user reveals it. */
  display: string;
  hasToken: boolean;
}

export interface SkillMeshSetupStep {
  body: string;
  command: SkillMeshCommand | null;
  footnote: string | null;
  title: string;
}

const TOKEN_FOOTNOTE = "Copy includes the full token even while it’s hidden.";

function plainCommand(text: string): SkillMeshCommand {
  return { copy: text, display: text, hasToken: false };
}

function tokenCommand(
  render: (token: string) => string,
  token: string,
  revealToken: boolean,
): SkillMeshCommand {
  return {
    copy: render(token),
    display: render(revealToken ? token : SKILL_MESH_TOKEN_MASK),
    hasToken: true,
  };
}

function step(
  title: string,
  body: string,
  command: SkillMeshCommand | null = null,
): SkillMeshSetupStep {
  return {
    body,
    command,
    footnote: command?.hasToken ? TOKEN_FOOTNOTE : null,
    title,
  };
}

/**
 * The client-specific setup steps. Token steps need the generated token;
 * without one there is nothing to run yet, so the list is empty.
 */
export function buildSkillMeshSetupSteps({
  client,
  endpoint,
  method,
  revealToken,
  token,
}: {
  client: SkillMeshClientId;
  endpoint: string;
  method: SkillMeshAuthMethod;
  revealToken: boolean;
  token: string | null;
}): SkillMeshSetupStep[] {
  const name = SKILL_MESH_SERVER_NAME;
  const resolved = resolveSkillMeshAuthMethod(
    getSkillMeshClient(client),
    method,
  );

  if (resolved === "token" && token === null) {
    return [];
  }

  if (client === "claude-code") {
    const add = `claude mcp add --transport http --scope user ${name} ${endpoint}`;
    if (resolved === "token" && token !== null) {
      return [
        step(
          "Add the server with your token",
          "Claude Code stores the token in its own config.",
          tokenCommand(
            (value) => `${add} \\\n  --header "Authorization: Bearer ${value}"`,
            token,
            revealToken,
          ),
        ),
        step(
          "Confirm the connection",
          `${name} should show as connected.`,
          plainCommand("claude mcp list"),
        ),
      ];
    }
    return [
      step(
        "Add the server",
        "Run this in any terminal. --scope user makes Skill Mesh available in every project.",
        plainCommand(add),
      ),
      step(
        "Sign in",
        `Start Claude Code, run /mcp, select ${name} and choose Authenticate. Your browser opens ARTI so you can approve access.`,
      ),
    ];
  }

  if (client === "codex-cli") {
    const add = `codex mcp add ${name} --url ${endpoint}`;
    if (resolved === "token" && token !== null) {
      return [
        step(
          "Put the token in your environment",
          "Add this to your shell profile, or load it from your secret manager. Codex reads the variable when it connects and never writes the token to config.",
          tokenCommand(
            (value) => `export ${SKILL_MESH_TOKEN_ENV_VAR}="${value}"`,
            token,
            revealToken,
          ),
        ),
        step(
          "Add the server",
          "Point Codex at the variable, not the token itself.",
          plainCommand(
            `${add} \\\n  --bearer-token-env-var ${SKILL_MESH_TOKEN_ENV_VAR}`,
          ),
        ),
      ];
    }
    return [
      step("Add the server", "Run this in any terminal.", plainCommand(add)),
      step(
        "Sign in",
        "Your browser opens ARTI so you can approve access.",
        plainCommand(`codex mcp login ${name}`),
      ),
    ];
  }

  if (client === "claude-desktop") {
    return [
      step(
        "Add a custom connector",
        "In Claude, open Customize → Connectors and choose Add custom connector. On Team and Enterprise plans, an owner adds it once under Organization settings → Connectors.",
      ),
      step(
        "Paste the endpoint",
        "Name it ARTI Skill Mesh and paste this URL. Leave Advanced settings empty.",
        plainCommand(endpoint),
      ),
      step(
        "Connect and approve",
        "Click Connect. Your browser opens ARTI so you can approve access.",
      ),
    ];
  }

  return [
    step(
      "Add the server",
      `In the Codex app, open Settings → MCP servers → Add server. Name it ${name}, choose Streamable HTTP and paste the endpoint. Or add this to ~/.codex/config.toml:`,
      plainCommand(`[mcp_servers.${name}]\nurl = "${endpoint}"`),
    ),
    step(
      "Sign in",
      "Run this once in a terminal. Your browser opens ARTI so you can approve access.",
      plainCommand(`codex mcp login ${name}`),
    ),
  ];
}

export const SKILL_MESH_WIZARD_STEPS = [
  { id: "client", label: "Choose client" },
  { id: "connect", label: "Connect" },
  { id: "verify", label: "Check it works" },
];

/**
 * Progress follows the user's real connections: connecting is done once a
 * token or sign-in grant exists, and checking is done once one has been used.
 */
export function getSkillMeshWizardProgress({
  connected,
  used,
}: {
  connected: boolean;
  used: boolean;
}): { completedStepIds: ReadonlySet<string>; currentStepId: string } {
  const completed = new Set(["client"]);
  if (connected) {
    completed.add("connect");
  }
  if (connected && used) {
    completed.add("verify");
  }
  return {
    completedStepIds: completed,
    currentStepId: connected ? "verify" : "connect",
  };
}

export function describeSkillMeshAccess(scopes: readonly string[]): string {
  if (scopes.includes(MCP_SKILLS_WRITE_SCOPE)) {
    return "Read and publish";
  }
  if (scopes.includes(MCP_SKILLS_READ_SCOPE)) {
    return "Read";
  }
  return "No Skill Mesh access";
}

export type SkillMeshConnectionTone = "active" | "expired" | "idle" | "waiting";

export interface SkillMeshConnectionRow {
  access: string;
  createdAt: string;
  id: string;
  kind: "grant" | "token";
  method: "Access token" | "Sign in";
  name: string;
  status: string;
  tone: SkillMeshConnectionTone;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const WAITING_FOR_FIRST_REQUEST = "Waiting for first request";

function daysLeftLabel(expiresAt: string, now: number): string {
  const days = Math.ceil((new Date(expiresAt).getTime() - now) / DAY_MS);
  return days === 1 ? "1 day left" : `${days} days left`;
}

function tokenStatus(
  token: DashboardMcpToken,
  now: number,
): Pick<SkillMeshConnectionRow, "status" | "tone"> {
  if (new Date(token.expires_at).getTime() <= now) {
    return { status: "Expired", tone: "expired" };
  }
  if (!token.last_used_at) {
    return { status: WAITING_FOR_FIRST_REQUEST, tone: "waiting" };
  }
  return {
    status: `Active · ${daysLeftLabel(token.expires_at, now)}`,
    tone: "active",
  };
}

/** The user's Skill Mesh connections, newest first. */
export function buildSkillMeshConnectionRows({
  grants,
  now,
  tokens,
}: {
  grants: readonly DashboardMcpGrant[];
  now: number;
  tokens: readonly DashboardMcpToken[];
}): SkillMeshConnectionRow[] {
  const rows: SkillMeshConnectionRow[] = [
    ...grants.map((grant) => ({
      access: describeSkillMeshAccess(grant.scopes),
      createdAt: grant.created_at,
      id: grant.grant_id,
      kind: "grant" as const,
      method: "Sign in" as const,
      name: grant.client_name,
      ...(grant.last_used_at
        ? { status: "Active", tone: "active" as const }
        : { status: WAITING_FOR_FIRST_REQUEST, tone: "waiting" as const }),
    })),
    ...tokens.map((token) => ({
      access: describeSkillMeshAccess(token.scopes),
      createdAt: token.created_at,
      id: token.token_id,
      kind: "token" as const,
      method: "Access token" as const,
      name: token.label,
      ...tokenStatus(token, now),
    })),
  ];
  return rows.toSorted(
    (left, right) =>
      new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime(),
  );
}

/** Placeholder row for the chosen client until its first connection exists. */
export function buildSkillMeshPendingRow({
  client,
  method,
  write,
}: {
  client: SkillMeshClient;
  method: SkillMeshAuthMethod;
  write: boolean;
}): SkillMeshConnectionRow {
  const resolved = resolveSkillMeshAuthMethod(client, method);
  return resolved === "token"
    ? {
        access: write ? "Read and publish" : "Read",
        createdAt: "",
        id: "pending",
        kind: "token",
        method: "Access token",
        name: client.label,
        status: "Not generated",
        tone: "idle",
      }
    : {
        access: "Set at sign-in",
        createdAt: "",
        id: "pending",
        kind: "grant",
        method: "Sign in",
        name: client.label,
        status: "Waiting for sign-in",
        tone: "waiting",
      };
}
